import { Suspense, lazy } from 'react'
import { useSearchParams } from 'react-router-dom'
import { PageHeader } from '@/components/layout'
import { DashboardFinanciero } from '@/components/reportes/dashboard/DashboardFinanciero'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { usePermissions } from '@/hooks/usePermissions'

const TableroRedes = lazy(() =>
  import('@/components/reportes/redes/TableroRedes').then((m) => ({ default: m.TableroRedes }))
)
const LiquidacionPagador = lazy(() =>
  import('@/components/reportes/pagadores/LiquidacionPagador').then((m) => ({ default: m.LiquidacionPagador }))
)
const PagadoresLista = lazy(() =>
  import('@/components/reportes/pagadores/PagadoresLista').then((m) => ({ default: m.PagadoresLista }))
)

type Pestana = 'finanzas' | 'redes' | 'pagadores'

const DESCRIPCION: Record<Pestana, string> = {
  finanzas: 'Cobrado, egresos y deuda del período',
  redes: 'Rendimiento de los reels y seguidores de Instagram',
  pagadores: 'Los pagadores, los cobros del mes a sus clientes y lo que se deben con el estudio',
}

/**
 * Reportes con las pestañas Finanzas, Redes y Pagadores. La pestaña activa vive en `?tab=`.
 * Pagadores se muestra solo con verHonorariosCliente. Sin ese permiso, `?tab=pagadores` abre Finanzas.
 * La liquidación de la pestaña Pagadores pide además verMovimientos.
 */
export function Reportes() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { puede } = usePermissions()
  const verPagadores = puede.verHonorariosCliente === true
  const tab = searchParams.get('tab')
  const pestana: Pestana =
    tab === 'redes' ? 'redes' : tab === 'pagadores' && verPagadores ? 'pagadores' : 'finanzas'

  const cambiarPestana = (valor: string) =>
    setSearchParams(valor === 'finanzas' ? {} : { tab: valor }, { replace: true })

  return (
    <div className="flex flex-col h-full">
      <PageHeader title="Reportes" description={DESCRIPCION[pestana]} />
      <main className="flex-1 p-4 md:p-6">
        <Tabs value={pestana} onValueChange={cambiarPestana} className="space-y-4 md:space-y-6">
          <TabsList>
            <TabsTrigger value="finanzas">Finanzas</TabsTrigger>
            <TabsTrigger value="redes">Redes</TabsTrigger>
            {verPagadores && <TabsTrigger value="pagadores">Pagadores</TabsTrigger>}
          </TabsList>
          <TabsContent value="finanzas">
            <DashboardFinanciero />
          </TabsContent>
          <TabsContent value="redes">
            <Suspense fallback={<Skeleton className="h-96 w-full" />}>
              <TableroRedes />
            </Suspense>
          </TabsContent>
          {verPagadores && (
            <TabsContent value="pagadores">
              <Suspense fallback={<Skeleton className="h-96 w-full" />}>
                <div className="space-y-4 md:space-y-6">
                  <PagadoresLista />
                  {puede.verMovimientos === true ? (
                    <LiquidacionPagador />
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      La liquidación de los pagadores pide el permiso para ver movimientos.
                    </p>
                  )}
                </div>
              </Suspense>
            </TabsContent>
          )}
        </Tabs>
      </main>
    </div>
  )
}
